"use client";

import { Music } from "lucide-react";

import LomdaUploadFile from "../shared/lomdaUploadFile";
import { Input } from "@/components/ui/input";
import { t } from "@/lib/localization";
import { AudioBlock as AudioBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import {
    LOMDA_DROPZONE_CLASS,
    LOMDA_DROPZONE_DRAG_CLASS,
} from "../shared/dropzone";
import { AUDIO_UPLOAD } from "../../../lib/uploadLimits";

export default function AudioBlock({ block }: { block: AudioBlockType }) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);

    const { url, name } = block.data;
    const editable = useBlockEditable();

    if (!url) {
        if (!editable) return null;
        return (
            <LomdaUploadFile
                reuse="audio"
                reuseLayout="canvas"
                options={{
                    ...AUDIO_UPLOAD,
                    className: `${LOMDA_DROPZONE_CLASS} h-32 w-full rounded-xl`,
                    dragClassName: LOMDA_DROPZONE_DRAG_CLASS,
                    title: { text: t("lomda-v3-editor.block.audio.upload-title"), keyName: "lomda-v3-editor.block.audio.upload-title" },
                    subtitle: { text: t("lomda-v3-editor.media.drop-hint"), keyName: "lomda-v3-editor.media.drop-hint" },
                }}
                onUploadFinished={(file) =>
                    updateBlock(block.id, { url: file.fileUrl, name: file.fileName })
                }
            />
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="lomda-v3-card-surface flex items-center gap-3 rounded-xl border p-3">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Music className="size-5" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {editable ? (
                        <Input
                            value={name}
                            onChange={(e) => updateBlock(block.id, { name: e.target.value })}
                            placeholder={t("lomda-v3-editor.audio.title")}
                            className="lomda-v3-page-text h-7 border-transparent px-1 lomda-v3-text-small shadow-none focus-visible:border-input"
                        />
                    ) : (
                        name && <span className="truncate lomda-v3-text-small">{name}</span>
                    )}
                    <audio controls src={url} className="h-9 w-full">
                        <track kind="captions" />
                    </audio>
                </div>
            </div>
        </div>
    );
}
